import { Component, inject, OnInit, Optional, ViewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { MaintenanceRoutineHomologousModel } from 'src/app/models/maintenance/maintenance-routine-homologous.model';
import { MaintenanceRoutineSupplyModel } from 'src/app/models/maintenance/maintenance-routine-supply.model';
import { MaintenanceRoutineModel } from 'src/app/models/maintenance/maintenance-routine.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { MaintenanceRoutineSupplyTableModel } from 'src/app/models/tables/maintenance/maintenance-routine-supply-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { MaintenanceRoutineHomologousService } from 'src/app/services/maintenance/maintenance-routine-homologous.service';
import { MaintenanceRoutineSupplyService } from 'src/app/services/maintenance/maintenance-routine-supply.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-maintenance-routine-supply',
  templateUrl: './maintenance-routine-supply.component.html',
  styleUrls: ['./maintenance-routine-supply.component.scss'],
  standalone: false
})
export class MaintenanceRoutineSupplyComponent implements OnInit {
  public config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly maintenanceRoutineSupplyService = inject(MaintenanceRoutineSupplyService);
  private readonly maintenanceRoutineHomologousService = inject(MaintenanceRoutineHomologousService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  @Optional() private ref = inject(DynamicDialogRef);

  @ViewChild('homologousModal') homologousModalComponent?: CustomModalComponent | null = null;
  activeTabIndex = 0;
  ascendant: boolean = true;
  ascendantSuppliesHomologous: boolean = true;
  articleObject = {
    code: 0,
    alterCode: "",
    name: ""
  };
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  columnSort: string | null = null;
  columnSortSuppliesHomologous: string | null = null;
  columns: ColumnResponseModel[] = [];
  columnsHomologous: ColumnResponseModel[] = [];
  columnsSuppliesHomologous: ColumnResponseModel[] = [];
  componentHomologous = CustomTableSelectComponent;
  data: PagedResponseModel<any> = new PagedResponseModel<any>();
  dataHomologous: PagedResponseModel<ArticleSelectionTableModel> = new PagedResponseModel<ArticleSelectionTableModel>();
  dataSuppliesHomologous: PagedResponseModel<ArticleSelectionTableModel> = new PagedResponseModel<ArticleSelectionTableModel>();
  disabledActions: boolean = false;
  filtersSuppliesHomologous: ColumnResponseModel[] = [];
  inputsForHomologous$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: false,
    filters: [],
    actions: []
  });
  isFormView: boolean = false;
  labelHomologous: string = 'Homólogos';
  limit: number = 5;
  limitSuppliesHomologous: number = 5;
  mainArticleId: number = 0;
  maintenanceRoutineModel: MaintenanceRoutineModel = new MaintenanceRoutineModel();
  maintenanceRoutineSupplyModel: MaintenanceRoutineSupplyModel = new MaintenanceRoutineSupplyModel();
  output$: Subject<any> = new Subject<any>();
  page: number = 1;
  pageSuppliesHomologous: number = 1;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Homólogos',
      icon: 'pi pi-th-large',
      action: 'homologous'
    },
  ];

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.maintenanceRoutineModel = inputs.maintenanceRoutineModel;
      this.articleObject = inputs.articleObject;
      this.disabledActions = !inputs.maintenanceRoutineModel.active;
    });

    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeSuppliesHomologous(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeSuppliesHomologous(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });

    if (!this.maintenanceRoutineModel.active) {
      this.tableActions = this.getDefaultActions();
    }
    await Promise.all([
      this.getColumns(),
      this.getMaintenanceRoutineSuppliesWithPagination()
    ])
  }

  addSupplies() {
    this.maintenanceRoutineSupplyModel = new MaintenanceRoutineSupplyModel();
    this.maintenanceRoutineSupplyModel.maintenanceRoutineId = this.maintenanceRoutineModel.maintenanceRoutineId;
    this.isFormView = true;
    this.handleTabChange(0);
  }

  private buildHomologous(item: any): MaintenanceRoutineHomologousModel {
    const homologous = new MaintenanceRoutineHomologousModel();
    homologous.maintenanceRoutineId = this.maintenanceRoutineModel.maintenanceRoutineId;
    homologous.articleId = parseInt(item.articleId);
    homologous.mainArticleId = this.mainArticleId;
    return homologous;
  }

  async getColumns() {
    try {
      this.columns = await firstValueFrom(this.maintenanceRoutineSupplyService.getColumnsAsync());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsHomologous() {
    try {
      this.columnsHomologous = await firstValueFrom(this.maintenanceRoutineHomologousService.getColumnsAsync());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsSuppliesHomologous() {
    try {
      this.columnsSuppliesHomologous = await firstValueFrom(this.maintenanceRoutineHomologousService.getColumnsForSuppliesAsync());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async deleteMaintenanceRoutineSupply(selectedIds: number[]) {
    if (selectedIds.length === 0) return;

    const confirmation = await this.confirmDialogService.confirm('¿Realmente desea eliminar el insumo de la rutina de mantenimiento?')
    if (confirmation) {
      try {
        await firstValueFrom(this.maintenanceRoutineSupplyService.deleteSomeSupplies(selectedIds));
        this.toastHandlerService.showToast('success', 'El insumo de la rutina de mantenimiento fue eliminado exitosamente.', 'Atención');
        this.mainArticleId = 0;
        await this.handleTabChange(0);
      } catch (error) {
        console.error('Error al eliminar el insumo de la rutina de mantenimiento .', error);
      }
    };

  }

  async deleteMaintenanceRoutineHomologous(selectedIds: number[]) {
    if (selectedIds.length === 0) return;

    const confirmation = await this.confirmDialogService.confirm('¿Realmente desea eliminar el homólogo del insumo?')
    if (confirmation) {
      try {
        await firstValueFrom(this.maintenanceRoutineHomologousService.deleteSomeHomologous(selectedIds));
        this.toastHandlerService.showToast('success', 'El homólogo del insumo fue eliminado exitosamente.', 'Atención');
        this.mainArticleId = 0;
        await this.handleTabChange(1);
      } catch (error) {
        console.error('Error al eliminar el homólogo del insumo.', error);
      }
    }
  }

  getDefaultActions(): TableAction[] {
    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        action: 'edit'
      }, {
        label: 'Homólogos',
        icon: 'pi pi-th-large',
        action: 'homologous'
      }
    ];
  }

  async getHomologousByMainArticle() {
    try {
      this.dataHomologous = await firstValueFrom(this.maintenanceRoutineHomologousService.getHomologousByMainArticle(this.maintenanceRoutineModel.maintenanceRoutineId,
        this.mainArticleId, this.page, this.limit, this.columnSort, this.ascendant));
    } catch (error) {
      console.error('Error al obtener la lista de los insumos homólogos.', error);
    }
  }

  async getMaintenanceRoutineSuppliesWithPagination() {
    try {
      this.data = await firstValueFrom(this.maintenanceRoutineSupplyService.getMaintenanceRoutineSuppliesWithPaginationAsync(this.maintenanceRoutineModel.maintenanceRoutineId,
        this.page, this.limit, this.columnSort, this.ascendant));
    } catch (error) {
      console.error('Error al obtener la lista de los insumos.', error);
    }
  }

  async getAllSuppliesHomologous() {
    try {
      this.dataSuppliesHomologous = await firstValueFrom(this.maintenanceRoutineHomologousService.getAllSuppliesHomologousAsync(this.maintenanceRoutineModel.maintenanceRoutineId, this.mainArticleId,
        this.pageSuppliesHomologous, this.limitSuppliesHomologous, this.columnSortSuppliesHomologous, this.ascendantSuppliesHomologous, this.filtersSuppliesHomologous));
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los insumos.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    if (this.activeTabIndex === tabIndex) return;
    this.activeTabIndex = tabIndex;
    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumns(),
          this.getMaintenanceRoutineSuppliesWithPagination()
        ]);
        this.data = {
          ...this.data,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsHomologous(),
          this.getHomologousByMainArticle()
        ]);
        this.dataHomologous = {
          ...this.dataHomologous,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  getFormView(event: boolean) {
    this.isFormView = event;
  }

  async onAction(event: { action: string, item?: MaintenanceRoutineSupplyTableModel | ArticleSelectionTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit') {
      switch (this.activeTabIndex) {
        case 0: {
          const response = await firstValueFrom(this.maintenanceRoutineSupplyService.getMaintenanceRoutineSupplyById(event.item!.id));
          if (response) {
            this.maintenanceRoutineSupplyModel = response;
            this.mainArticleId = response.articleId;
          }
          this.getFormView(true);
          break;
        }
      }
    }

    if (event.action === 'bulk-delete' && event.selectedItems) {
      switch (this.activeTabIndex) {
        case 0:
          await this.deleteMaintenanceRoutineSupply(event.selectedItems);
          this.getMaintenanceRoutineSuppliesWithPagination()
          break;
        case 1:
          await this.deleteMaintenanceRoutineHomologous(event.selectedItems);
          this.getHomologousByMainArticle()
          break;
      }
    }

    if (event.action === 'homologous') {
      const supply = event.item! as MaintenanceRoutineSupplyTableModel;
      const label = `Homólogos - ${supply.articleCode} - ${supply.articleName}`;
      this.labelHomologous = this.getLabelBreakdown(label);
      this.mainArticleId = supply.articleId;
      await this.handleTabChange(1);
    }
  }

  getLabelBreakdown(label: string): string {
    if (label.length > 75) {
      return `${label.slice(0, 75 - 3)}...`;
    }
    return label;
  }

  async handleSelectedItem(selectedItem: any) {
    if (!selectedItem?.length) return;
    const homologous = selectedItem.map((item: any) => this.buildHomologous(item));
    if (homologous.length > 0) {
      try {
        const response = await firstValueFrom(
          this.maintenanceRoutineHomologousService.createMultipleHomologousAsync(homologous, this.maintenanceRoutineModel.maintenanceRoutineId, this.mainArticleId));
        if (response) {
          this.toastHandlerService.showToast('success', 'Los artículos homólogos fueron creados exitosamente', '');
          this.getHomologousByMainArticle();
        } else {
          this.toastHandlerService.showToast('error', 'Hubo un error al crear los artículos homólogos', '');
          this.dataHomologous = new PagedResponseModel<ArticleSelectionTableModel>();
        }

      } catch (error) {
        console.error('Error al crear los artículos homólogos.', error);
      }
    } else {
      this.dataHomologous = new PagedResponseModel<ArticleSelectionTableModel>();
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getMaintenanceRoutineSuppliesWithPagination();
        break;
      case 1:
        await this.getHomologousByMainArticle();
        break;
    }
  }

  async onPageChangeSuppliesHomologous(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageSuppliesHomologous = event.page;
    this.limitSuppliesHomologous = event.pageSize;
    this.columnSortSuppliesHomologous = event.sortField || null;
    this.ascendantSuppliesHomologous = event.sortOrder === 1;
    await this.getAllSuppliesHomologous();
  }

  async onFilterChangeSuppliesHomologous(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersSuppliesHomologous = this.removeDuplicates(filters);
    this.filtersSuppliesHomologous.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }


  async openHomologousModal() {
    this.filtersSuppliesHomologous = [];
    await Promise.all([
      this.getColumnsSuppliesHomologous(),
      this.getAllSuppliesHomologous()]
    );
    this.updateInputs();
    this.homologousModalComponent && this.homologousModalComponent.show();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersSuppliesHomologous = this.filtersSuppliesHomologous.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersSuppliesHomologous.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInputs() {
    this.inputsForHomologous$.next({
      headers: this.columnsSuppliesHomologous,
      data: this.dataSuppliesHomologous,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      limitPaginator: true,
      filters: this.filtersSuppliesHomologous,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }
}
